import axios from "axios";
import { toast } from "sonner";
import { isPrintActive } from "@/lib/printActive";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SEC-09: the session lives in an httpOnly cookie — JS never sees or stores the token,
// so an XSS has nothing to steal from storage. No localStorage token path exists anymore.
const api = axios.create({ baseURL: API, withCredentials: true });

// Stable per-browser id so the server can tell a known device from a new one
// without re-alerting on routine browser/OS updates.
function getDeviceId() {
  let id = localStorage.getItem("fas_device_id");
  if (!id) {
    id = (crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("fas_device_id", id);
  }
  return id;
}

// Double-submit CSRF: the readable csrf_token cookie is echoed back as a header on
// every request; the server compares them for cookie-authenticated mutations.
function getCsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

api.interceptors.request.use((cfg) => {
  const csrf = getCsrfToken();
  if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  cfg.headers["X-Device-Id"] = getDeviceId();
  return cfg;
});

// ── THREE DIFFERENT FACTS, THREE DIFFERENT MESSAGES ──────────────────────────────────
// 1. UNREACHABLE  — no answer at all, or a gateway/origin failure. The session is FINE.
// 2. SESSION ENDED — a real 401 from the server. Log in again.
// 3. CSRF MISMATCH — the server answered and refused one action. Still signed in.
// Conflating #1 with #2 is what dumped the crew at a login screen on a wifi hiccup.
//
// 502/503/504 are the classic three. 520-527 and 530 are CLOUDFLARE's own codes, meaning the
// edge could not get a usable answer out of our server. 524 is the one the crew actually hits:
// the edge gives up on any request that runs past its read timeout, which is exactly what a long
// AI photo read does. 524 was NOT in this set, so it was read as "the server answered, it is
// alive" — the amber bar was CLEARED, a save that failed went SILENT, and the browser's own
// transport text reached the screen as a confusing error. (Report 2026-10-03, api.js:41 finding.)
const GATEWAY_DOWN = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530]);
let reachable = true;
const reachSubs = new Set();

function setReachable(next) {
  if (reachable === next) return;
  reachable = next;
  if (next) writeFails = 0;   // recovered: the next outage starts its own count at one
  reachSubs.forEach((fn) => { try { fn(reachable); } catch { /* a subscriber must not break transport */ } });
}

export const isServerReachable = () => reachable;
export function onServerReachability(fn) { reachSubs.add(fn); return () => reachSubs.delete(fn); }

// A failed SAVE must never be swallowed. A plain per-message throttle would show ONE warning
// for TWO lost changes — a tech tapping Save twice during an outage would reasonably assume the
// second one went through. So failed writes are COUNTED and the single toast is rewritten in
// place with the running count: one voice on screen, every failure represented in it.
let writeFails = 0;
let writeFailAt = 0;
const WRITE_FAIL_TOAST = "server-unreachable-write";
const WRITE_FAIL_MS = 9000;
function noticeWriteFailed() {
  if (Date.now() - writeFailAt > WRITE_FAIL_MS) writeFails = 0;   // old toast is gone; start fresh
  writeFails += 1;
  writeFailAt = Date.now();
  toast.error(
    writeFails === 1
      ? "Couldn't reach the server — that change was NOT saved. You are still signed in; when the amber bar clears, do it again."
      : `Couldn't reach the server — ${writeFails} changes were NOT saved. You are still signed in; when the amber bar clears, do them again.`,
    { id: WRITE_FAIL_TOAST, duration: WRITE_FAIL_MS },
  );
}

api.interceptors.response.use(
  (res) => { setReachable(true); return res; },
  (err) => {
    if (axios.isCancel?.(err) || err.code === "ERR_CANCELED") return Promise.reject(err);
    const st = err.response?.status;

    // A CRASH PAGE IS NOT OUR SERVER SPEAKING. When a worker is killed mid-request, or the edge
    // times out, the body is an HTML error page — not our JSON. Nothing downstream may read
    // fields off it: that is how an HTML blob turns into a parse error in front of the crew.
    // And a 5xx carrying one is an infrastructure failure whatever number is printed on it,
    // including codes this file never anticipated.
    const htmlBody = typeof err.response?.data === "string" && /^\s*</.test(err.response.data);
    if (htmlBody) err.response.data = { detail: null };

    const unreachable = !err.response || GATEWAY_DOWN.has(st) || (st >= 500 && htmlBody);
    if (st && !unreachable) setReachable(true);   // the server answered for itself: it is alive

    if (st === 401 && !window.location.pathname.startsWith("/login")) {
      // Never hard-logout mid-print: a Browser Print run is local and would die if
      // the app unmounts. The flag is time-bounded so this can't defer forever.
      if (isPrintActive()) return Promise.reject(err);
      window.location.assign("/login?expired=1");
      return Promise.reject(err);
    }
    if (st === 403 && /csrf/i.test(JSON.stringify(err.response?.data?.detail ?? ""))) {
      // Kept as its OWN fact so nothing conflates it with "logged out" or "offline". No extra
      // toast here: the server's own refusal text already reaches the user at the call site.
      err.csrfMismatch = true;
      return Promise.reject(err);
    }
    if (unreachable) {
      err.serverUnreachable = true;
      setReachable(false);
      // A read that fails is covered by the banner; a WRITE that fails must say out loud
      // that the change did not save, so nothing is silently lost. Auth screens carry their
      // own honest message (nothing was being "saved"), so they are left to speak for themselves.
      const m = (err.config?.method || "get").toLowerCase();
      // `checksItself`: the screen re-reads what actually happened and says so — a slow answer is not a lost save.
      if (m !== "get" && !String(err.config?.url || "").includes("/auth/") && !err.config?.checksItself) noticeWriteFailed();
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  // No message from the server to relay. Call sites only ever hand over the `detail`, so this
  // is the ONE place that can tell "the server refused and said nothing" from "the server was
  // never reached" — and a vague "something went wrong" on top of an outage is exactly the
  // kind of message that hides the truth from the person on the floor.
  if (detail == null) {
    return reachable
      ? "That didn’t go through, and the server gave no reason. Try it once more — if it happens again, tell the office what you were doing."
      : "Can’t reach the server — that didn’t go through. You’re still signed in; we’re retrying.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

// One message for an error, with the three states kept apart — so a login screen can say
// "the server is unreachable" instead of blaming the password.
export function apiErrorMessage(err) {
  if (err?.serverUnreachable) {
    return "Can’t reach the server — that’s a connection problem, not your password. We’re retrying automatically; try again in a moment.";
  }
  if (err?.csrfMismatch) {
    return "Security check failed — your session is still active. Reload this page and try again.";
  }
  // NOT err.message: that is the browser's transport string ("Request failed with status code
  // 524", or a JSON parse complaint about an HTML crash page). Those are the words the crew was
  // reading and could do nothing with. Only the server's own `detail`, or a plain sentence.
  return formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong. Please try again.";
}

export default api;
